import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CSVLink } from "react-csv";
import axios from "axios";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const Attendance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId } = useParams();
  const courseID = courseId;

  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [openAddClassDialog, setOpenAddClassDialog] = useState(false);
  const [newClass, setNewClass] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/course/get-all-attendance-dates-of-a-course`,
          { courseID: courseID },
          { withCredentials: true }
        );
        const attendance = response.data.dates || [];
        setClasses(attendance);
        console.log(attendance);

        if (attendance.length > 0) {
          // Fetch students from the first quiz
          const firstAttencance = attendance[0].name;
          const getStudents = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/course/get-all-attendance-given-date`,
            { date: firstAttencance, courseID: courseID },
            { withCredentials: true }
          );

          const students = getStudents.data.attendance.map(
            (student) => student.studentID
          );
          setStudents(students || []);
          
          // Fetch scores for all quizzes in parallel
          const attendanceResponses = await Promise.all(
            attendance.map((attendance) =>
              axios.post(
                `${process.env.REACT_APP_BASE_URL}/course/get-all-attendance-given-date`,
                { date: attendance.name, courseID: courseID },
                { withCredentials: true }
              )
            )
          );
          console.log(attendanceResponses);
          // Process scores data and update state immutably
          let allScores = {};
          attendanceResponses.forEach((attendanceResponse, index) => {
            console.log(attendanceResponse.data);
            (attendanceResponse.data.attendance || []).forEach((item) => {
              const studentID = item.studentID;
              const date = attendance[index].date;
              const marks = item.attendance.present;
              console.log(studentID, date, marks);
              if (!allScores[studentID]) {
                allScores[studentID] = {};
              }
              allScores[studentID][date] = marks;
            });
          });

          setAttendance(allScores);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching quiz data:", error);
        setLoading(false);
      }
    };

    if (courseID) {
      fetchAttendanceData();
    }
  }, [courseID]);

  const updateAttendance = (rollNumber, className, value) => {
    try {
      const response = axios.post(
        `${process.env.REACT_APP_BASE_URL}/course/edit-attendance`,
        {
          courseID: courseID,
          studentID: rollNumber,
          date: className,
          attendance: value,
        },
        { withCredentials: true }
      );
      if (response.status === 200) {
        const updatedAttendance = { ...attendance };
        updatedAttendance[rollNumber] = updatedAttendance[rollNumber] || {};
        updatedAttendance[rollNumber][className] = value;
        setAttendance(updatedAttendance);
      }
    } catch (err) {
      console.log("Error updating score");
    }
  };

  const handleAddClass = async() => {
    if (!newClass) return; 
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/course/add-attendance-date`,
        {
          courseID: courseID,
          date: newClass,
        }, {withCredentials: true}
      );
      if (response.status === 200) {
        setClasses([...classes, {date: newClass}]);
        setNewClass("");
        setOpenAddClassDialog(false);
      }
    } catch (error) {
      console.error("Error adding quiz:", error);
    }
  };

  const exportData = () => {
    return students.map((student) => {
      const row = { RollNumber: student.rollNumber, Name: student.name };
      classes?.forEach((className) => {
        row[className] = attendance[student.rollNumber]?.[className] || "";
      });
      return row;
    });
  };

  if(loading) {return <h1>loading...</h1>}

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2, backgroundColor: "#fff" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={() => navigate("/attendance")}
            sx={{ color: "#904dd3", mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <h2 style={{ color: "#904dd3", margin: 0 }}>
            {courseID.split('-')[0]} - Section {courseID?.split("-")[2]} (
            {courseID?.split("-")[1]}) Attendance
          </h2>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setOpenAddClassDialog(true)}
            sx={{
              backgroundColor: "#904dd3",
              "&:hover": { backgroundColor: "#7c40b5" },
            }}
          >
            Add Class
          </Button>
          <CSVLink
            data={exportData()}
            filename="attendance.csv"
            style={{ textDecoration: "none" }}
          >
            <Button
              startIcon={<FileDownloadIcon />}
              variant="contained"
              sx={{
                backgroundColor: "#904dd3",
                "&:hover": { backgroundColor: "#7c40b5" },
              }}
            >
              Export CSV
            </Button>
          </CSVLink>
        </div>
      </div>

      <TableContainer sx={{ maxHeight: "70vh" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}
              >
                Roll Number
              </TableCell>
              {classes.map((className, index) => (
                <TableCell
                  key={index}
                  sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}
                >
                  {className.date}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {students.map((student, index) => (
              <TableRow key={index} hover>
                <TableCell>{student}</TableCell>
                {classes.map((className, classIndex) => (
                  <TableCell key={classIndex}>
                    <TextField
                      size="small"
                      value={attendance[student]?.[className.date] || ""}
                      onChange={(e) => {
                        // Update the scores state as the user types
                        const newScore = e.target.value;
                        setAttendance((prevScores) => ({
                          ...prevScores,
                          [student]: {
                            ...prevScores[student],
                            [className.date]: newScore,
                          },
                        }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          updateAttendance(student, className.date, e.target.value);
                        }
                      }}
                      sx={{ width: "100px" }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openAddClassDialog}
        onClose={() => setOpenAddClassDialog(false)}
      >
        <DialogTitle>Add New Class</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Class Name"
            fullWidth
            value={newClass}
            onChange={(e) => setNewClass(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddClassDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddClass}
            variant="contained"
            sx={{ backgroundColor: "#904dd3" }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Attendance;
